import type { ErrorStateProps } from '../components/ui/ErrorState';

type ErrorStateType = NonNullable<ErrorStateProps['type']>;

interface ErrorStateOptions {
  fallbackTitle?: string;
  fallbackMessage?: string;
  statusCode?: number;
}

const DEFAULT_MESSAGES: Record<ErrorStateType, { title: string; message: string }> = {
  'server-error': {
    title: 'Oops! Something went wrong',
    message: 'An unexpected error occurred. Please try again in a few moments.',
  },
  'bad-gateway': {
    title: 'Bad Gateway',
    message: 'The server encountered a temporary error. Please try again shortly.',
  },
  'timeout': {
    title: 'Request Timeout',
    message: 'The server took too long to respond. Please try again in a few moments.',
  },
  'network-error': {
    title: 'Connection Problem',
    message: 'Unable to connect to the server. Please check your internet connection and try again.',
  },
  'not-found': {
    title: 'Not Found',
    message: 'The requested resource could not be found.',
  },
};

const normalize = (value?: string | null) => value?.toLowerCase().trim() ?? '';

const stripHtml = (value: string): string => {
  const withoutComments = value.replace(/<!--[\s\S]*?-->/g, ' ');
  const withoutTags = withoutComments.replace(/<[^>]+>/g, ' ');
  const decoded = withoutTags
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");
  return decoded.replace(/\s+/g, ' ').trim();
};

export const sanitizeErrorMessage = (value?: string | null, fallback?: string): string => {
  if (!value) {
    return fallback ?? '';
  }

  const cleaned = stripHtml(value);
  if (cleaned) {
    return cleaned;
  }
  return fallback ?? '';
};

export const deriveErrorState = (
  errorMessage?: string | null,
  options: ErrorStateOptions = {},
): { type: ErrorStateType; title: string; message: string } => {
  const normalized = normalize(errorMessage);
  const { fallbackTitle, fallbackMessage, statusCode } = options;

  let type: ErrorStateType = 'server-error';

  if (statusCode === 0 || normalized.includes('network') || normalized.includes('connection')) {
    type = 'network-error';
  } else if (statusCode === 504 || normalized.includes('timeout') || normalized.includes('504')) {
    type = 'timeout';
  } else if (statusCode === 502 || normalized.includes('bad gateway') || normalized.includes('502') || normalized.includes('gateway')) {
    type = 'bad-gateway';
  } else if (normalized.includes('not found') || normalized.includes('no data') || normalized.includes('does not exist')) {
    type = 'not-found';
  }

  const defaultConfig = DEFAULT_MESSAGES[type];
  const finalTitle = fallbackTitle || defaultConfig.title;
  const rawMessage = fallbackMessage || errorMessage || defaultConfig.message;
  const finalMessage = sanitizeErrorMessage(rawMessage, defaultConfig.message);

  return {
    type,
    title: finalTitle,
    message: finalMessage,
  };
};
